import { flattenThemeTokens } from '@/styles/theme/themeSchema'
import themePresets, { availableThemes } from '@/styles/theme/presets'

const STORAGE_KEYS = {
  preset: 'themePreset',
  mode: 'themeMode',
  darkMode: 'darkMode',
}

const DEFAULT_PRESET = 'profiteia'
const DEFAULT_MODE = 'light'

function getRoot() {
  return typeof document !== 'undefined' ? document.documentElement : null
}

export function getAvailableThemes() {
  return availableThemes
}

export function getThemePreset(id = DEFAULT_PRESET) {
  return themePresets[id] || themePresets[DEFAULT_PRESET]
}

export function getStoredTheme() {
  const fallbackMode = typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEYS.darkMode) === 'true'
    ? 'dark'
    : DEFAULT_MODE

  return {
    preset: typeof localStorage !== 'undefined'
      ? localStorage.getItem(STORAGE_KEYS.preset) || DEFAULT_PRESET
      : DEFAULT_PRESET,
    mode: typeof localStorage !== 'undefined'
      ? localStorage.getItem(STORAGE_KEYS.mode) || fallbackMode
      : DEFAULT_MODE,
  }
}

function persistTheme(theme) {
  if (typeof localStorage === 'undefined') {
    return
  }

  localStorage.setItem(STORAGE_KEYS.preset, theme.preset)
  localStorage.setItem(STORAGE_KEYS.mode, theme.mode)
  localStorage.setItem(STORAGE_KEYS.darkMode, String(theme.mode === 'dark'))
}

function applyTokenVariables(root, tokens) {
  const entries = Object.entries(flattenThemeTokens(tokens))

  entries.forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })
}

export function applyTheme({ preset = DEFAULT_PRESET, mode = DEFAULT_MODE, persist = true } = {}) {
  const root = getRoot()
  const resolvedPreset = getThemePreset(preset)
  const resolvedMode = resolvedPreset.modes[mode] ? mode : DEFAULT_MODE
  const theme = {
    preset: resolvedPreset.id,
    mode: resolvedMode,
  }

  if (persist) persistTheme(theme)

  if (!root) {
    return theme
  }

  root.dataset.theme = theme.preset
  root.dataset.mode = theme.mode
  root.classList.toggle('dark', theme.mode === 'dark')

  applyTokenVariables(root, resolvedPreset.modes[theme.mode])

  // CSS-tokens are now live on <html>; refresh Highcharts defaults from them
  // so chart backgrounds/axes/tooltips track the active mode. Loaded lazily so
  // chart-free surfaces (e.g. the admin portal) don't pull the Highcharts
  // bundle onto their critical path; the customer app loads Highcharts
  // asynchronously at boot anyway, so deferring this call is a no-op there.
  // Surfaces that never render charts can opt out entirely by setting
  // `window.__PF_NO_HIGHCHARTS__ = true` before bootstrap.
  if (typeof window === 'undefined' || !window.__PF_NO_HIGHCHARTS__) {
    import('@/plugins/highcharts')
      .then(({ applyHighchartsTheme }) => applyHighchartsTheme())
      .catch(() => {})
  }

  return theme
}

export function setThemePreset(id) {
  const stored = getStoredTheme()
  return applyTheme({ preset: id, mode: stored.mode })
}

export function setThemeMode(mode) {
  const stored = getStoredTheme()
  return applyTheme({ preset: stored.preset, mode })
}

export function toggleThemeMode() {
  const stored = getStoredTheme()
  return applyTheme({
    preset: stored.preset,
    mode: stored.mode === 'dark' ? 'light' : 'dark',
  })
}

export const themeStorageKeys = STORAGE_KEYS
export const defaultThemePreset = DEFAULT_PRESET
export const defaultThemeMode = DEFAULT_MODE
